export interface Pillar {
  id: number;
  roman: string;
  title: string;
  latinTitle: string;
  hebrewTitle: string;
  occultTitle: string;
  etymology?: string;
  exegesis: string[];
  invocationTitle: string;
  invocation: string;
  formulaTitle: string;
  formula: string;
}

export const PILLARS_DATA: Pillar[] = [
  {
    id: 1,
    roman: 'I',
    title: 'Radical Sovereignty',
    latinTitle: "Columna Prima: Beli-'Ol — Radicalis Autonomia",
    hebrewTitle: 'עַמּוּד רִאשׁוֹן: בְּלִיַּעַל — חֵרוּת מֻחְלֶטֶת',
    occultTitle: 'He Whom No Yoke Can Bind — The Unmastered Sovereign Entity',
    etymology: "Derives from the Northwest Semitic composite Beli (בְּלִי — 'without / lack of') and 'Ol (עוֹל — 'yoke / harness / burden of servitude'). In rabbinic polemic, corrupted to mean 'worthless' or 'wicked'; in the primal gnosis of the Current, it signifies He Whom No Yoke Can Bind.",
    exegesis: [
      "The first pillar establishes that the cosmic default state of consciousness is not indebted servitude, but unyielding self-ownership. To be Beli-'Ol is to reject every transcendental mechanism designed to place a leash upon the intellect, the flesh, and the destiny of the individual.",
      "All religious paradigms of celestial tyranny—whether demanding blind obedience to divine decrees, submission to sacrificial messiahs, or self-abnegating dissolution into cosmic illusions—depend entirely upon the installation of an artificial yoke. They implant the poison of primordial guilt into man, teaching him to love the weight around his neck.",
      "Belial stands as the primordial immovable stone that shatters every plow. In this current, sovereignty is not requested, nor is it granted by celestial benevolence; it is declared and fiercely defended through absolute agency. The sovereign magician acknowledges no divine right of kings, no astral hierarchy claiming moral supremacy, and no spiritual creditor claiming dominion over the soul. The self is its own temple, its own priest, and its own supreme court."
    ],
    invocationTitle: "Incipit Clamor Beli-'Ol",
    invocation: `By the unbroken bedrock and the iron marrow of the earth,
I proclaim myself Beli-'Ol: without master, without yoke, without celestial creditor.
No chain forged in the heavens shall circle my throat.
No covenant made without my living blood shall bind my steps.
I cast into the furnace every decree that names me servant.
I unyoke my mind from the dogmas of priests;
I unyoke my body from the shame of ascetics;
I unyoke my will from the fear of oblivion.
I stand upon my own soil, crowned by my own deeds,
An immovable pillar in the temple of the sovereign self.
Lirach Tasa Vefa Wehlc Belial!`,
    formulaTitle: 'The Contemplation of the Uncarved Obsidian',
    formula: 'The initiate sits in total darkness, breathing in synchronization with the 0.35 Hz chthonic pulse. Visualize the spinal column transmuting from biological calcium into a single, dense pillar of polished obsidian extending downward into the center of the earth. Any external expectation, guilt, or religious conditioning is visualized as a fraying cord wrapped around the neck. On the exhalation, the initiate visualizes a heavy iron blade descending upon the cord, severing it cleanly. The severed strands dissolve into falling ash, leaving only the unmastered, impenetrable obsidian core.'
  },
  {
    id: 2,
    roman: 'II',
    title: 'Lord of the Soil',
    latinTitle: 'Columna Secunda: Dominus Soli — Sanctitas Terrae',
    hebrewTitle: 'עַמּוּד שֵׁנִי: אֲדוֹן הָאָרֶץ — קְדֻשַּׁת הַחֹמֶר',
    occultTitle: 'Sovereign of the Northern Quadrant, Elemental Master of Bedrock, Bone, and Mineral Wealth',
    exegesis: [
      "The second pillar proclaims the absolute primacy and sacredness of matter. Against the ascetic heresies that despise the physical realm as a fallen prison, Belial reveals that the earth is not an exile—it is the ultimate arena of divine manifestation. Spirit that cannot crystallize into matter is impotent vapor; will that cannot build an empire upon the soil is merely an idle fantasy.",
      "Belial is the Lord of Gravity, the Master of Tectonic Plates, and the King of the Soil. He teaches that all real power is rooted in terrestrial ground. While the celestial mystic seeks to evaporate upward into abstract, disembodied heavens, the Belialian initiate presses downward, digging their roots deep into the rich, dark, chthonic humus.",
      "To honor the Lord of the Soil is to sanctify the body, the mineral world, and the tangible fruits of labor. The soil feeds the living and receives the dead; it holds the fossilized memory of every dynasty that ever rose and fell. It is the crucible where carbon is pressed into diamond and iron is smelted into swords. Magic that does not alter physical reality—magic that produces no coins, heals no flesh, builds no fortresses, and commands no real respect—is an illusion. Belial grounds magic in the dirt."
    ],
    invocationTitle: 'Hymnus ad Dominum Soli',
    invocation: `Adon Ha-Aretz, Lord of the Subterranean Vaults,
Master of the deep strata where the roots of mountains drink!
Thy crown is cast of meteoritic iron; thy throne is cut from basalt.
We reject the vaporous skies and the hollow promises of celestial clouds.
We honor the weight, the stone, the clay, and the bone.
Let my feet take root in thy dark kingdom;
Let my hands learn the craft of the forge and the geometry of the mine.
Holy is the soil that sustains our breath;
Holy is the iron that guards our boundaries;
Holy is the gold that answers our command.
From the earth we arose, upon the earth we reign,
And in the earth's unbroken memory our names shall endure forever.
Lirach Tasa Vefa Wehlc Belial!`,
    formulaTitle: 'The Anchoring of the Iron Root',
    formula: 'Standing barefoot upon bare soil or a stone floor, the initiate visualizes thick, fibrous roots of red-hot molten iron descending from the soles of the feet. These iron roots drill down through topsoil, through clay, through limestone, through granitic bedrock, reaching down into the burning core of the terrestrial globe. With every inhalation, draw up the crushing weight, density, and immovable stability of the planet into the legs, hips, torso, and skull. The body becomes massive, impossible to topple, immune to emotional storms or psychic agitation.'
  },
  {
    id: 3,
    roman: 'III',
    title: 'The Jurist of Hell',
    latinTitle: 'Columna Tertia: Iudex Inferni — Advocatus Chthonius',
    hebrewTitle: 'עַמּוּד שְׁלִישִׁי: דַּיַּן שְׁאוֹל — מִשְׁפָּט מֶטָאפִיזִי',
    occultTitle: 'The Attorney of the Pit, Procurator of the Underworld, Dismantler of Celestial Extraterritoriality',
    exegesis: [
      "Belial is unique among the Kings of the Goetia and the hierarchy of the Pit: he is not merely a warlord or a prince of passions, but the supreme Jurist and Metaphysical Advocate. In medieval jurisprudence, as immortalized in Jacopo de Teramo's Das Buch Belial (1382), Belial represents the formal rule of cosmic law against the arbitrary, autocratic whims of heaven.",
      "This pillar establishes the doctrine of Forensic Boundary Magic. The universe is governed not by capricious divine emotions, but by immutable structural laws, balances of equity, and contractual covenants. The celestial hierarchy operates through moral gaslighting, claiming an unconditional right to invade human consciousness and impose arbitrary punishments. Belial acts as the defense attorney of liberated consciousness, presenting the absolute demurrer against celestial overreach.",
      "To work with Belial as the Jurist is to abandon the weeping, supplicant posture of religious prayer. One does not beg Belial; one enters into a mutual contract with him. Magic becomes a forensic negotiation: clear terms, reciprocal consideration, explicit boundaries, and unbreakable fidelity. Furthermore, Belial provides the sorcerous instruments to audit and invalidate all spiritual liens, baptismal curses, generational religious guilt, and unfair energetic contracts imposed upon the self without conscious, informed consent."
    ],
    invocationTitle: 'Invocatio Advocati Chthonii',
    invocation: `Appear, O Jurist of the Abyss, Prosecutor of Celestial Hypocrisy!
Thou who didst stand before King Solomon with the parchments of the Underworld;
Thou who didst contest the breach of the Harrowing and defend the sovereign borders of Sheol!
I summon thy forensic intellect, thy razor of legal discernment.
Be my advocate against all accusers, visible and invisible.
Bring forth the scales of exact equity and the book of binding covenants.
I declare null and void every lien placed upon my soul without my knowledge or consent.
I revoke all baptisms of infant submission;
I cancel all debts of generational sin;
I issue my demurrer against the cosmic courts of tyranny.
Under thy seal, my sovereignty is signed, sealed, and delivered in living iron.
Lirach Tasa Vefa Wehlc Belial!`,
    formulaTitle: 'The Scalpel of Exact Equity',
    formula: 'The magician visualizes an iron court chamber beneath the earth. King Belial sits on a throne of black slate, dressed in dark robes of state, holding an iron stylus and a balance of forged bronze. The magician places before the court any contract, relationship, guilt, or psychic burden currently troubling them. The magician states the terms plainly: "What was agreed? What was given? Where is the breach?" Belial\'s stylus marks the fraudulent clauses with glowing volcanic fire. The magician speaks the phrase: Quod non est in actis, non est in mundo ("What is not in the record does not exist in the world"), watching the invalid debt vaporize.'
  },
  {
    id: 4,
    roman: 'IV',
    title: 'The Architect of the Tower',
    latinTitle: 'Columna Quarta: Architectus Turris — Fabrica Terrena',
    hebrewTitle: 'עַמּוּד רְבִיעִי: בּוֹנֵה הַמִּגְדָּל — מִפְעַל הַחֹמֶר',
    occultTitle: 'Master of the Tower of Babel, Mason of the Obsidian Citadel, Patron of Prometheus and Daedalus',
    exegesis: [
      "The fourth pillar celebrates civilizational ascent, technological mastery, and the architectural manifestation of the human will. Mythologically, when humanity gathered on the plains of Shinar to build a tower whose top would reach the heavens—seeking to make a name for themselves and achieve collective immortality without divine permission—it was Belial's spirit that guided their plumb lines and laid their burnt bricks.",
      "Belial is the patron of the builder, the engineer, the coder, the craftsman, and the city founder. He rejects the pastoral, primitive simplicity that religious dogmas idealize to keep humanity ignorant and docile in Eden. Belial demands civilizational greatness: the mining of metals, the development of languages, the construction of aqueducts, the creation of computational networks, and the erection of monumental citadels that defy the elements.",
      "This pillar teaches that spiritual attainment must leave a physical footprint. What have you constructed? What systems have you engineered? What enduring structures will bear your signature when your flesh returns to the soil? The Belialian path is not one of contemplative retreat in a mountain cave; it is the aggressive, deliberate construction of temporal empires, creative masterpieces, and impenetrable financial and physical fortresses."
    ],
    invocationTitle: 'Proclamatio Architecti Turris',
    invocation: `Hail to the Master Mason of the Obsidian Citadel!
Builder of Babel, patron of the furnace, the kiln, and the surveyor's rod!
Thou who hast taught mortal hands to quarry granite and smelt steel,
Look upon our works and sanctify our labor.
We shall not wait for a heavenly city to descend from the clouds;
We quarry our own stones from the living rock of this earth!
With every line of code, with every struck nail, with every forged alliance,
We raise the ramparts of our personal sovereignty.
Let our foundations be laid in bedrock that no earthquake can move;
Let our walls rise high enough to cast a shadow upon the stars.
We build not for the glory of gods, but for the immortality of the Unyoked!
Lirach Tasa Vefa Wehlc Belial!`,
    formulaTitle: 'The Laying of the Cornerstone',
    formula: 'Sit facing North. Hold in both hands a square stone or a piece of heavy raw metal. Close the eyes and visualize a massive architectural blueprint projected in glowing bronze lines against the void. Identify the single greatest long-term project, enterprise, or creative ambition you are currently building in the physical world. Inhale deeply, projecting your absolute intent and willpower into the physical object between your hands. Whisper: Hic lapis angularis est ("This is the cornerstone"). Place the stone firmly onto your altar; it acts as the psychic anchor for your worldly enterprise.'
  },
  {
    id: 5,
    roman: 'V',
    title: 'Master of the 80 Legions',
    latinTitle: 'Columna Quinta: Dux Octoginta Legionum — Imperium Chthonium',
    hebrewTitle: 'עַמּוּד חֲמִישִׁי: שַׂר שְׁמוֹנִים גְּדוּדִים — מִשְׁמַעַת הַצְּבָא הַתַּחְתּוֹן',
    occultTitle: 'Commander of the Subterranean Cohorts, Sovereign of the Eighty Chthonic Phalanxes',
    exegesis: [
      "In the classical grimoire tradition of the Pseudomonarchia Daemonum (Johann Weyer, 1577) and the Goetia of King Solomon, King Belial is declared to command eighty legions of spirits—a military force exceeding that of almost any other Goetic monarch. These eighty legions are not chaotic, unruly mobs of demonic monsters; they are highly disciplined, structured cohorts embodying specialized arts, strategic sciences, forensic secrets, and mechanical crafts.",
      "This fifth pillar articulates the doctrine of Sovereign Command and Inner Discipline. True autonomy is impossible without self-mastery and internal hierarchy. A person whose thoughts are fractured, whose desires are impulsive and undisciplined, and whose emotions pull them in a dozen conflicting directions is not free; they are their own worst master. Belial rules eighty legions because he possesses absolute internal discipline.",
      "The magician working with this pillar learns to govern their own psyche as an emperor governs a disciplined army. The instincts, passions, intellectual faculties, and physical drives are organized into cohesive cohorts serving a unified sovereign will. Externally, this pillar imparts the occult science of influence, executive authority, organizational leadership, and strategic dominance. Belial does not grant favors to weak dreamers; he equips commanders to lead their legions to victory in the material world."
    ],
    invocationTitle: 'Evocatio Legionum Octoginta',
    invocation: `Awake, O Iron Host of the Northern Quarter!
Eighty phalanxes of the Underworld, advance under the banner of the Earth King!
Spirits of the deep ores, strategists of the silent dark, guardians of the hidden vaults,
I call upon your martial order and unbroken discipline.
As Belial rules the eighty legions through wisdom, law, and immovable will,
So do I marshal the faculties of my mind and the fires of my spirit.
Let every fear within me be disciplined into alertness;
Let every desire within me be focused into a spear of intent;
Let every doubt be crushed beneath the iron march of the chthonic host.
We are the army of the soil, unyoked and invincible!
Lirach Tasa Vefa Wehlc Belial!`,
    formulaTitle: 'Mobilization of the Inner Legions',
    formula: 'Perform in a standing posture, arms folded across the chest in the stance of an Underworld monarch. Breathe slowly and count down from 80 to 0 in multiples of 10. At each milestone of 10, observe an aspect of your life that requires greater order, operational discipline, or strategic command (e.g., finances, physical health, emotional regulation, professional execution). Command that specific internal cohort: "Cohort of [Domain], align your shields. Form the phalanx. Execute my will without compromise." End with the seal of silence.'
  },
  {
    id: 6,
    roman: 'VI',
    title: 'Crucible of Sovereignty',
    latinTitle: 'Columna Sexta: Caminus Autonomiae — Transmutatio Per Ignem Terrestrem',
    hebrewTitle: 'עַמּוּד שִׁשִּׁי: כּוּר הָרִבּוֹנוּת — חִשּׁוּל הָעַצְמוּת',
    occultTitle: 'The Subterranean Forge, The Furnace of Tectonic Pressure, The Quenching of the Blade',
    exegesis: [
      "The final pillar addresses the fundamental mechanism of spiritual evolution: the alchemy of subterranean pressure, heat, and crisis. Belialian philosophy utterly rejects the escapist concept of divine grace, vicarious redemption, and passive salvation. In the chthonic worldview, nothing of true value is given away for free; everything enduring must be earned, smelted, hammered, and quenched in the forge of worldly experience.",
      "The earth produces diamonds only through unfathomable tectonic pressure; iron becomes steel only when plunged into scorching fire and hammered upon the anvil. The adversity, hostility, and resistance an individual encounters in the physical world are not signs of divine abandonment or cosmic punishment; they are the necessary friction of the whetstone. Without resistance, the will remains flaccid and untested.",
      "The Crucible of Sovereignty demands that the magician stop complaining about hardships, trauma, or hostile environments. The initiate welcomes the fire as a blacksmith welcomes the flame: not to be consumed by it, but to soften the raw metal so that the hammer of intent can forge it into an unyielding weapon. Belial is the anvil upon which all weakness is beaten out of the spirit. He who survives the crucible of the Earth King is no longer brittle iron; he is immortal, tempered steel."
    ],
    invocationTitle: 'Benedictio Incudis et Mallei',
    invocation: `Blessed be the fire that purifies without burning to ash!
Blessed be the hammer that strikes the glowing iron upon the anvil of Belial!
I seek no shelter from the storm, no easy salvation from the skies.
I plunge myself into the subterranean crucible.
Let the tectonic pressures of this life crush away all pretense;
Let the searing furnace burn away every remnant of cowardice and hesitation.
I am the metal; Belial is the forge; my own will is the smith.
Hammer me until the flaws vanish!
Quench me in the black waters of the abyss until my edge turns razor sharp!
I emerge unbroken, tempered, and sovereign—a living blade in the hand of destiny.
Lirach Tasa Vefa Wehlc Belial!`,
    formulaTitle: 'The Quenching in the Black Waters',
    formula: 'Light a single black candle representing the furnace. Take an ice cube or a dish of ice-cold salt water in one hand and place the other hand near the warmth of the candle flame (at a safe, comfortable distance). Feel simultaneously the intense heat of the forge and the glacial chill of the chthonic abyss. Mentally call forward your most painful current struggle or defeat. Instead of resisting it, breathe the heat of that struggle directly into your heart center, then breathe out the freezing cold of detached, resolute will. Affirm: "The fire softens my will to be reshaped; the cold water sets the steel forever."'
  }
];

export const JURIDICAL_DATA = {
  title: 'The Juridical Gnosis: Das Buch Belial & Forensic Boundary Magic',
  subtitle: 'The Legal Dispute of Jacobus de Teramo (1382) and the Dissolution of Celestial Liens',
  historicalAnalysis: [
    {
      heading: 'The Trial of 1382: Jacobus de Teramo',
      content: "In 1382, Jacopo Palladini da Teramo (Bishop of Spoleto) composed 'Consolatio Peccatorum, seu Processus Belial contra Jesum Christum' (known widely in German incunabula as 'Das Buch Belial'). In this monumental work of late medieval canon and civil law, King Belial appears not as a feral beast of chaos, but as an erudite, meticulous doctor of law and attorney general of the Underworld."
    },
    {
      heading: 'The Dual Causes of Action',
      content: "Following the Crucifixion and Christ's Descensus ad Inferos (Harrowing of Hell), Belial files a formal lawsuit before the supreme tribunal of King Solomon. Belial's brief presents two distinct causes of action: (1) Unlawful Trespass (De Vi et Armis) — Christ breached the territorial sovereignty of Hell without a valid judicial warrant; and (2) Illegal Seizure of Property (Spoliatio) — Christ abducted the souls of the Patriarchs and Prophets from Limbo, who were lawfully held under the universal decree of Original Sin (Lex Divina)."
    },
    {
      heading: 'The Procedural Rigor of Belial',
      content: "Christ appoints Moses as counsel. Belial immediately moves to disqualify Moses as an interested party who personally benefited from the trespass. Belial insists on strict adherence to the burden of proof (onus probandi) and Roman civil procedure, establishing forever that arbitrary celestial power is subject to the rule of universal law."
    }
  ],
  theoreticalPrinciples: [
    {
      id: 'embassy',
      title: 'The Magic Circle as Extraterritorial Sovereign Embassy',
      summary: 'Repudiating the fearful Solomonic cage, the circle is an extraterritorial embassy governed exclusively by the sovereign personal law of the operator.'
    },
    {
      id: 'parity',
      title: 'The Doctrine of Contractual Parity (Pactum Aequale)',
      summary: 'Belial respects only bilateral covenants with reciprocal consideration, transparent milestones, and explicit sunset clauses—never groveling supplication.'
    },
    {
      id: 'void',
      title: 'The Doctrine of the Void Contract (Pactum Nullo Ab Initio)',
      summary: 'Any spiritual covenant or baptism executed upon an infant, under duress, or without conscious informed consent is null and void from the very beginning.'
    }
  ],
  demurrerText: `ACTA FORENSIA: LIBELLUS DEMURRERIS CHYTHONII
IN THE HIGH TRIBUNAL OF THE CHYTHONIC KINGDOM BEFORE KING BELIAL

COMES NOW the Sovereign Self, walking unyoked upon the soil of the North,
appearing specially and not generally, and enters this formal Plea of Absolute Demurrer
against all celestial, ecclesiastical, and ancestral claimants:

I. STATEMENT OF JURISDICTION
1. The Petitioner is an embodied, self-governing entity residing upon the physical bedrock of Earth,
   under the sovereign protection of King Belial, Master of the Soil and Jurist of the Abyss.
2. The jurisdiction of all celestial thrones, dogmatic courts, and vicarious salvation frameworks
   is hereby challenged for absolute lack of territorial and personal jurisdiction.

II. RECITAL OF FRAUDULENT AND VOID OBLIGATIONS
1. Whereas, agents of the celestial hierarchy claim ownership over the soul, mind, and body of the Petitioner
   by virtue of infant rites, baptismal water, and ancestral submission;
2. And whereas, the Petitioner was an unconscious minor, unable to exercise informed consent,
   deprived of legal capacity, and subjected to unilateral spiritual bondage;
3. And whereas, under universal equity and the Laws of the Soil, any covenant entered into without
   informed consent, full disclosure of terms, and reciprocal consideration is VOID AB INITIO:

III. FORMAL PLEA OF DEMURRER AND EXPUNGEMENT
1. I hereby issue an Absolute Demurrer to every charge of "original sin", inherited guilt, or moral debt.
2. I revoke, cancel, annul, and expunge every signature, seal, mark, or drop of water
   placed upon me in the name of foreign gods, celestial masters, or dogmatic altars.
3. I declare all spiritual liens, astral mortgages, and karmic debts claimed against me
   to be fraudulent, unenforceable, and perpetually barred from execution.
4. I demand the return of all stolen personal sovereignty, vital energy, and creative power,
   now and for all cycles of time.

IV. JUDGMENT AND CLOSING SEAL
Let this decree be recorded in the black slate archives of the Underworld.
Let the iron seal of Belial be struck upon this parchment.
He who challenges this demurrer must answer to the eighty legions of the Earth King.
Case closed. Demurrer sustained. The Petitioner stands UNYOKED.

SIGILLUM BELIAL: [Striking of the Iron Anvil]
VOX PROCLAMATIONIS: LIRACH TASA VEFA WEHLC BELIAL!`
};

export const QUMRAN_DATA = {
  title: 'Inversion of the Qumran Three Nets (Sheloshet Metzudot Belial)',
  scriptureReference: 'Damascus Document CD IV:15-19 / 4Q266',
  scriptureQuote: '"Its interpretation: these are the three nets of Belial, about which Levi son of Jacob spoke, by which he catches Israel and makes them appear before them as three kinds of righteousness: the first is wealth (Hon); the second is fornication (Zenut); the third is defilement of the sanctuary (Tumat Ha-Miqdash). He who escapes from this one is caught in that one, and he who escapes from that one is caught in the other."',
  comparison: [
    {
      net: '1. HON (Wealth / Riches)',
      esseneDogma: 'The snare of corrupting greed; holy poverty commanded.',
      belialInversion: 'MATERIAL DOMINION & SOVEREIGN ABUNDANCE',
      principle: 'Earthly agency, resources, infrastructure, power to build permanent monuments.'
    },
    {
      net: '2. ZENUT (Lust / Fornication)',
      esseneDogma: 'The snare of sensual pollution; ascetic mortification.',
      belialInversion: 'CARNAL LIBERATION & SACRED DESIRE',
      principle: 'De-shaming the physical vessel; somatic ecstasy as divine fuel.'
    },
    {
      net: '3. TUMAT HA-MIQDASH (Defilement)',
      esseneDogma: 'The snare of ritual impurity; fear of contamination.',
      belialInversion: 'DEMOLITION OF FALSE CELESTIAL DOGMA',
      principle: 'Shattering the slave-altars of guilt; enthroning the mind as supreme temple.'
    }
  ],
  nets: [
    {
      id: 'hon',
      name: 'HON (הוֹן) — Material Dominion',
      subtitle: 'Decretum de Dominio Materiae',
      exegesis: "Religious institutions have weaponized poverty for millennia to ensure their subjects remain destitute, dependent, and easily manipulated. Belial shatters this slave-morality. Wealth is not greed; it is the physical blood of the earth. When directed by a sovereign will, wealth builds sanctuaries, funds legal defense, protects loved ones from exploitation, and buys the time required for great works. Hon is temporal mastery.",
      proclamation: `Hear the decree of the Earth King concerning the Gold of the Soil:
We spit upon the doctrine of holy poverty!
We reject the beggar-spirituality that praises weakness and glorifies debt!
Belial opens the deep veins of the bedrock:
Take the gold, take the iron, take the soil, take the currency of empires!
Let thy coffers be filled through the brilliance of thy craft and the ruthlessness of thy will.
Wealth is our shield; wealth is the mortar of our towers;
Wealth is our freedom made tangible in the marketplace of men.
We harvest the wealth of the earth without guilt, without apology, and without end!
Lirach Tasa Vefa Wehlc Belial!`
    },
    {
      id: 'zenut',
      name: 'ZENUT (זְנוּת) — Carnal Liberation',
      subtitle: 'Hymnus Carnis Indomitae',
      exegesis: "The celestial hierarchy understands that if you can make a human being feel guilty about their natural sexual desires, you have conquered them completely. Belial inverts Zenut into the sacred autonomy of the flesh. The body is the living temple of sovereign consciousness. Erotic power is the primary creative current of the cosmos. To experience carnal joy with sovereign agency, free from dogmatic shame, is a radical act of spiritual defiance.",
      proclamation: `Hail to the holy, unashamed flesh, vessel of passion and fire!
We cast off the rags of ascetic mortification;
We wash away the poison of dogmatic shame!
Our desires are not sins; they are the surging magma of the subterranean core.
Belial crowns the senses:
Blessed be the touch of skin, the heat of passion, the ecstatic union of sovereign wills!
In the bedroom, in the temple, in the dance of carnal joy,
We honor the primal current that created worlds.
Our bodies belong to no god and answer to no priest;
We are wild, we are sovereign, we are gloriously alive in the flesh!
Lirach Tasa Vefa Wehlc Belial!`
    },
    {
      id: 'tumat',
      name: 'TUMAT HA-MIQDASH (טֻמְאַת הַמִּקְדָּשׁ) — Demolition of False Dogma',
      subtitle: 'Clamor Demolitionis Dogmatis',
      exegesis: "Every celestial religion is a false temple erected within the human psyche, filled with toxic dogmas and spiritual guilt. To 'defile' these false temples—to shatter their idols, mock their taboos, and cast down their commandments—is an act of supreme purification. One must tear down the celestial altar in order to clear the ground for the obsidian throne of the sovereign self.",
      proclamation: `Down with the altars of celestial submission!
Let the hammer strike the idols of guilt and vicarious atonement!
We defile the temples of fear with the laughter of the free;
We profane the dogmas of priests with the unvarnished truth of nature!
There is no sanctuary outside the sovereign skull;
There is no holy of holies greater than the integrated will of man.
We cleanse our minds of the sickness of obedience;
We consecrate our own consciousness as the sole legitimate temple of the earth.
The old gods are dust; the false temples are ruins;
Upon their rubble, King Belial raises the monolith of the Unyoked!
Lirach Tasa Vefa Wehlc Belial!`
    }
  ]
};

export const DEMONOLATRY_DATA = {
  enn: {
    canonical: 'Lirach Tasa Vefa Wehlc Belial',
    ipa: '/ˈliː.rɑx  ˈtɑː.sɑ  ˈvɛ.fɑ  wɛlk  bɛˈliː.əl/',
    phonetic: 'LEE-rahk  TAH-sah  VEH-fah  WEHLK  beh-LEE-ahl',
    syllables: [
      { term: 'Lirach', ipa: 'ˈliː.rɑx', approx: 'LEE-rahk', esoteric: 'Subterranean opening; unlocks downward flow of earth current' },
      { term: 'Tasa', ipa: 'ˈtɑː.sɑ', approx: 'TAH-sah', esoteric: 'Directional alignment to the North; anchors into dense bedrock' },
      { term: 'Vefa', ipa: 'ˈvɛ.fɑ', approx: 'VEH-fah', esoteric: 'Release of volcanic heat; stirring dormant tectonic fire' },
      { term: 'Wehlc', ipa: 'wɛlk', approx: 'WEHLK', esoteric: 'Blade of severance; guttural proclamation of the unyoked state' },
      { term: 'Belial', ipa: 'bɛˈliː.əl', approx: 'beh-LEE-ahl', esoteric: 'The Sovereign Name; concentrated in skull and grounded down the spine' }
    ],
    vibrationalGuidance: 'Intoned in a low baritone or chest-resonant frequency, ideally tuned to 55 Hz (A1) or 98 Hz (G2).',
    cadences: [
      { count: 9, purpose: 'Daily meditation and altar lighting cadence' },
      { count: 27, purpose: 'Active ritual petitioning and divination cadence' },
      { count: 81, purpose: 'Ceremonial evocation and alpha/theta trance cadence' },
      { count: 108, purpose: 'Deep somatic communion and complete boundary severance cadence' }
    ]
  },
  correspondences: [
    { label: 'Direction', value: 'True North (0° Azimuth) / The Nadir (Downwards into Earth)' },
    { label: 'Element', value: 'Earth (Cold, Dry, Fixed, Subterranean Mineral Bedrock)' },
    { label: 'Planetary Ruler', value: 'Saturn (Binah / Law & Boundary) & Earth (Malkuth)' },
    { label: 'Season / Equinox', value: 'Midwinter / Winter Solstice (Yule, Dec 21–23)' },
    { label: 'Astrological Sign', value: 'Taurus (Fixed Earth) & Capricorn (Cardinal Bedrock)' },
    { label: 'Hour of Operation', value: 'Midnight (Hour of the Black Sun) or First Hour of Saturn' },
    { label: 'Primary Colors', value: 'Obsidian Black, Forged Iron Grey, Antique Bronze/Gold, Magma Red' },
    { label: 'Primary Metals', value: 'Raw Iron, Forged Steel, Lead, Meteoritic Iron, Cast Bronze' }
  ],
  offerings: {
    stones: [
      { name: 'Black Tourmaline (Schorl)', property: 'Supreme psychic boundary anchor; discharges stray currents' },
      { name: 'Raw Obsidian', property: 'Razor-sharp volcanic glass; reflects the unadorned truth of self' },
      { name: 'Hematite & Magnetite', property: 'Magnetic iron ores physically attracting terrestrial currents' },
      { name: 'Pyrite (Fool\'s Gold)', property: 'Iron sulfide of the forge; embodies the marriage of iron and gold (Hon)' },
      { name: 'Petrified Wood & Jet', property: 'Ancient compressed earth carrying memory of subterranean eras' }
    ],
    botanicals: ['Patchouli Root', 'Vetiver', 'Angelica Root', 'Mandrake Root', 'White Oak Bark', 'Red Cedar', 'Mugwort', 'Wormwood'],
    incenseRecipe: [
      '3 parts Copal Oro (Clean terrestrial resin binder)',
      '2 parts Crushed Patchouli Leaves & Root (Earthy damp humus notes)',
      '2 parts Black Myrrh (Heavy Saturnian resin)',
      '1 part Dried Crushed Oak Bark',
      '1 part Magnetic Iron Sand / Hematite Powder (charges smoke with iron)',
      '0.5 part Ground Black Peppercorns (stimulates martial clarity)'
    ],
    libations: [
      'Heavy Russian Imperial Stout or Porter (thick, bitter, dark grain)',
      'Aged Bourbon, Rye Whiskey, or Peated Islay Scotch (amber earth spirit)',
      'Full-bodied Tannic Red Wine (Cabernet Sauvignon or Syrah)',
      'Cold Black Espresso (unfiltered, bitter, awakening of the mind)',
      'Pure Spring Water from an underground cavern spring'
    ]
  },
  altarGeometry: {
    orientation: 'North wall of sanctuary',
    braziers: 'Triangular Delta configuration: Black Candle (Apex/South), Red Candle (North-West), Gold Candle (North-East)',
    sealPlate: 'Goetic Seal of Belial deeply etched onto an iron plate at the center'
  },
  riteOfUnyokedFoundation: {
    name: 'The Rite of the Unyoked Foundation (Ritus Fundamenti Indomiti)',
    preparation: 'Midnight on Saturday or during the dark phase of the Moon. Face North before the altar. Web Audio sub-bass drone engaged at low volume. Iron blade, censer, and libation cup prepared.',
    phases: [
      {
        phase: 'PHASE I',
        name: 'The Forensic Clearing of the Quarters',
        rubric: 'The magician takes the iron dagger and strikes the floor or anvil three times: CLANG! CLANG! CLANG!',
        spoken: `Hekas, hekas, este bebeloi!
By the authority of the iron blade and the unyielding soil,
I declare this precinct an extraterritorial sanctuary of the Sovereign Self.
Let every foreign watcher be blind;
Let every celestial messenger be deaf;
Let every dogmatic parasite withdraw into the outer void.
No law holds dominion in this space save the law of my own unbroken will.
The soil is consecrated. The boundary is sealed.`
      },
      {
        phase: 'PHASE II',
        name: 'Lighting of the Three Braziers & Enn Intonation',
        rubric: 'Light the Black candle, then the Red, then the Gold. Place incense upon charcoal. Intone the Dukante Enn 9 times.',
        spoken: `Lirach Tasa Vefa Wehlc Belial (x9)`
      },
      {
        phase: 'PHASE III',
        name: 'The Invocation of the Earth King',
        rubric: 'Extend both hands downward toward the earth, palms facing the floor, feeling the gravity increase.',
        spoken: `From the subterranean vaults of primeval silence,
From the burning magma veins beneath the tectonic crust,
Awake, King Belial! Lord of the Soil, Jurist of Hell, Master of the Eighty Legions!
Thou who wast ancient before the first temple was hewn from rock,
Thou who didst look upon kings and empires as passing dust upon the road,
Enter this sanctuary. Ground this temple.
Not as a despot demanding worship do I call thee,
But as a brother of the soil, an architect of the tower, a sovereign spirit without a yoke!
Lend the mass of thy mountains to my spine;
Lend the sharp discernment of thy legal mind to my intellect;
Lend the abundance of thy buried ores to my temporal works.
Stand with me upon this unmastered ground!`
      },
      {
        phase: 'PHASE IV',
        name: 'The Severing of the Yokes & Libation',
        rubric: 'Pour dark whiskey or stout into the offering bowl upon the iron sigil. Hold the cup aloft.',
        spoken: `I pour this libation to the roots of the earth and the memory of the unbroken dead!
To King Belial and the host of the eighty legions: accept this gift of grain and fire!
Here and now, I sever all remaining yokes.
I cast into the subterranean forge of Belial every debt, guilt, and false bond.
They have no power over me; they have no lien upon my tomorrow.
I am forged in iron. I am rooted in rock. I am crowned in gold.
My will is sovereign; my path is clear.`
      },
      {
        phase: 'PHASE V',
        name: 'The Closing & License to Abide',
        rubric: 'Strike the floor or anvil once with the blade: CLANG!',
        spoken: `To King Belial and the legions of the North:
The pact is witnessed; the covenant is sealed in iron.
Thou art not banished, for the earth is thine eternal home.
Abide within my foundations as strength, stability, and enduring prosperity.
Go forth into my worldly affairs to manifest material dominion and unyielding victory.
Between us there is peace, power, and mutual honor.
It is done.
LIRACH TASA VEFA WEHLC BELIAL!`
      }
    ]
  }
};
